import { ReactNode, useEffect } from "react";
import { Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import ReactGA from "react-ga4";
import { isEmpty } from "lodash";
import classnames from "classnames";

// Mui components
import { Typography, Container, Box, Button } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { Theme } from "@mui/material/styles";

// Context & Hooks
import useAuth from "utils/hooks/useAuth";
import { useAbility } from "@casl/react";
import { AbilityContext } from "context/Ability";
import { useAppSelector, useAppDispatch } from "app/hooks";

// Services
import { logout } from "services";

// Store
import { getUserOrgs, fetchUserOrgsThunk } from "store/slice/sysadmin/userOrgs/userOrgsSlice";

// Enums
import icons from "enums/icons";
import { urlPaths } from "enums/urlPaths";

// Constants
import { REDIRECT_SELECT_HUB } from "constants/constants";

// Components
import Home from "components/Home";
import CustomIcon from "./CustomIcon";
import Loader from "components/Loader";
import { Toast } from "components/Toast";
import NotFound from "components/NotFound";
import Loading from "components/Layout/Loading";
import { SideBar } from "components/Layout/SideBar";
import ReportStatusLoader from "./ReportStatusLoader";
import MainLayout from "components/Layout/MainLayout";

const useStyles = makeStyles((theme: Theme) => ({
    appBody: {
        padding: 0,
        marginLeft: 284,
        width: `calc(100% - 284px)`,
        "&::before": {
            content: '""',
            width: "100%",
            height: 250,
            position: "fixed",
            top: 0,
            left: 0,
            zIndex: -99999,
        },
    },
    appBodyPace: {
        marginLeft: 284,
        width: `calc(100% - 284px)`,
    },
}));

const AuthWrapper = ({ children }: { children: ReactNode }) => {
    const classes = useStyles();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const { pathname, search } = useLocation();
    const { hasSession, sessionData, loggedInUserData, fetched, error } = useAuth();

    const ability = useAbility(AbilityContext);
    const userOrgs = useAppSelector(getUserOrgs);

    const isLoading = (fetched && error && error.code === 401) || (!fetched && !hasSession);
    const hasBasicRoles =
        ability.can("see", "LD_FREE") ||
        ability.can("see", "LD_BASIC") ||
        ability.can("see", "LD_LIMITED") ||
        ability.can("see", "LD_TALENT") ||
        ability.can("see", "PACE") ||
        ability.can("see", "PACE_ASSESSMENT");

    useEffect(() => {
        const getUserOrgs = async () => {
            await dispatch(fetchUserOrgsThunk());
        };

        if (!userOrgs.data.length && userOrgs.status === "pristine") {
            getUserOrgs();
        }

        if (
            userOrgs.data.length &&
            sessionData !== null &&
            hasBasicRoles &&
            sessionData.authn.user.org_selected.id === null
        ) {
            navigate(REDIRECT_SELECT_HUB);
            return;
        }
    }, [dispatch, hasBasicRoles, navigate, sessionData, userOrgs.data.length, userOrgs.status]);

    return (
        <>
            {hasSession && hasBasicRoles && (
                <>
                    <SideBar pathname={`${pathname}${search}`} loggedInUserData={loggedInUserData} />
                    <Container
                        maxWidth={false}
                        className={classnames(classes.appBody, {
                            [classes.appBodyPace]: pathname.includes("pace"),
                        })}
                    >
                        {children}
                    </Container>
                </>
            )}

            {isLoading && <Loading />}

            {hasSession && fetched && isEmpty(error) && !hasBasicRoles && (
                <Box
                    width="100vw"
                    height="100vh"
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                >
                    <CustomIcon icon={icons.lock}></CustomIcon>
                    <Typography color="text.primary" variant="h4">
                        Access restricted
                    </Typography>
                    <Typography color="text.disabled" variant="h6" sx={{ fontSize: 16 }}>
                        You don&lsquo;t have enough permission to view this page. Please contact the administrator.
                    </Typography>
                    <Button variant="text" onClick={() => logout()}>
                        Log out
                    </Button>
                </Box>
            )}
        </>
    );
};

const App = (): JSX.Element => {
    const location = useLocation();

    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: location?.pathname });
    }, [location]);

    return (
        <>
            <Loader />
            <Toast />
            <ReportStatusLoader />
            <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/choose-you-hub" element={<Navigate to={urlPaths.ChooseYourHub} />} />
                <Route path="/leadership-dynamics" element={<Navigate to={urlPaths.Search} />} />
                <Route path="/account-management" element={<Navigate to={urlPaths.AccountManagement} />} />
                <Route
                    path="/choose-your-hub/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/choose-your-hub" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/leadership-dynamics/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/leadership-dynamics" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/pace/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/pace" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/talent/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/talent" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/account-management/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/account-management" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/hub-management/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/hub-management" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path="/user-management/*"
                    element={
                        <AuthWrapper>
                            <MainLayout parentRoute="/user-management" />
                        </AuthWrapper>
                    }
                />
                <Route
                    path={urlPaths.NotFound}
                    element={
                        <AuthWrapper>
                            <NotFound />
                        </AuthWrapper>
                    }
                />
                <Route path="*" element={<Navigate to={urlPaths.NotFound} />} />
            </Routes>
        </>
    );
};

export default App;
